import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ListOrderedIcon } from "lucide-react";
import { Button, DialogClose } from "@mindstonehq/ui";
import { classNames } from "~/utils";
import { BulbIcon } from "~/components/icons/bulb-icon";
import { TRIVIA_STEPS } from "../annotate/annotate-trivia-bar";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

interface QuestionChoiceProps {
  earnablePoints: {
    mcq?: number;
    freeText?: number;
  };
  choices: TRIVIA_STEPS[];
  skippable?: boolean;
  nextStep: () => void;
}

export default function QuestionChoice({
  earnablePoints,
  choices,
  skippable,
  nextStep,
}: QuestionChoiceProps) {
  const { t } = useLingui();
  const [selected, setSelected] = useState<TRIVIA_STEPS | null>(
    choices.length === 1 ? choices[0] : null,
  );

  const buttons = useMemo(() => {
    const questionChoices = [];
    if (choices.includes(TRIVIA_STEPS.QUESTION_MCQ)) {
      questionChoices.push({
        step: TRIVIA_STEPS.QUESTION_MCQ,
        text: t(msg`Multiple Choice Question`),
        icon: ListOrderedIcon,
        ep: earnablePoints.mcq || 0,
      });
    }
    return questionChoices;
  }, [earnablePoints, choices, t]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.4 } }}
      exit={{ opacity: 0 }}
      className="w-full h-full md:h-auto flex flex-col flex-1 items-center md:justify-start space-y-5 lg:space-y-8"
    >
      <div className="w-full flex flex-col items-center space-y-6">
        <div className="rounded-lg bg-secondary h-12 w-12 flex items-center justify-center">
          <BulbIcon className="text-primary" />
        </div>
        <div className="flex flex-col items-center">
          <h3><Trans>Trivia Time</Trans></h3>
          {choices.length > 1 && (
            <div className="text-placeholder-foreground leading-7">
              <Trans>Choose Your Challenge</Trans>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col flex-1 md:flex-none space-y-3 w-full md:w-[400px]">
        {buttons.map((button, index) => (
          <button
            className={classNames(
              "w-full sm:h-20 p-4 rounded-md flex flex-col sm:flex-row items-center gap-4",
              choices.length === 1
                ? "sm:h-auto sm:flex-col bg-muted text-foreground pointer-events-none"
                : button.step === selected
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-foreground hover:bg-muted-hover",
            )}
            key={index}
            onClick={() => setSelected(button.step)}
          >
            <div
              className={classNames(
                "h-12 w-12  flex items-center justify-center rounded-lg",
                choices.length === 1
                  ? "bg-muted-foreground text-background"
                  : button.step === selected
                    ? "bg-background text-primary"
                    : "bg-muted-foreground text-background",
              )}
            >
              <button.icon className="h-6 w-6" />
            </div>
            <div
              className={classNames(
                "flex flex-col",
                choices.length > 1 ? "sm:items-start" : "",
              )}
            >
              <div className="text-lg font-semibold leading-7">
                {button.text}
              </div>
              <div
                className={classNames(
                  choices.length === 1
                    ? "text-muted-foreground"
                    : button.step === selected
                      ? "text-primary-foreground"
                      : "text-muted-foreground",
                )}
              >
                <Trans>
                  1 question, up to{" "}
                  <span className="font-medium">{Math.round(button.ep)}ep</span>
                </Trans>
              </div>
            </div>
          </button>
        ))}
      </div>
      <div className="flex items-center space-x-4">
        {skippable && (
          <DialogClose asChild>
            <Button className="w-full sm:w-auto" variant="outline" size="lg">
              <Trans>Not now</Trans>
            </Button>
          </DialogClose>
        )}

        <Button
          className="w-full sm:w-auto"
          variant="secondary"
          size="lg"
          onClick={nextStep}
          disabled={!selected}
        >
          <Trans>Quiz Me!</Trans>
        </Button>
      </div>
    </motion.div>
  );
}
